<template>
  <div
    class="mb-6 flex h-[176px] w-full items-center justify-center rounded-md bg-[#FAFAFA] p-[9px]"
  >
    <div class="size-[140px]">
      <Thumbnail :type="thumbType" :src="previewImageUrl" />
    </div>
  </div>

  <div
    v-for="(info, index) in fileInformation"
    :key="index"
    class="flex h-[30px] w-full items-center justify-between border-b border-[#E5E5E5] text-[12px] leading-[16px] text-gray-800"
  >
    <div class="opacity-60">{{ info.label }}</div>
    <div class="font-secondary">{{ info.value }}</div>
  </div>

  <div
    class="flex min-h-[30px] w-full items-start justify-between border-b border-[#E5E5E5] py-1.5 text-[12px] leading-[16px] text-gray-800"
  >
    <div class="opacity-60">Timestamp</div>
    <div class="text-right">
      <div v-if="isInactive" class="font-secondary leading-[18px]">—</div>
      <template v-else>
        <div
          v-for="registration in displayedRegistrations"
          :key="registration.origin.id"
          class="flex items-center justify-end last:mb-0"
        >
          <Avatar
            v-if="registration.origin.flag.url"
            :src="registration.origin.flag.url"
            :size="12"
            class="mr-1.5 shrink-0"
          />
          <DacrMarkIcon v-else class="mr-1.5 size-3 text-gray-800" />
          <div class="mr-3 font-secondary leading-[18px] text-gray-800">
            {{ formatTimestamp(registration.timestamp) }}
          </div>
          <a
            v-if="canViewCertificates && registration.certificateUrl"
            :href="registration.certificateUrl"
            target="_blank"
            class="flex items-center gap-x-1"
          >
            <FileTextIcon class="size-3 text-green-700" />
            <ChevronRightIcon class="size-2.5 text-green-700" />
          </a>
        </div>
      </template>
      <div
        v-if="!isInactive && hasMoreRegistrations"
        class="mt-1 flex cursor-pointer items-center justify-end gap-x-1.5 text-[12px] font-medium leading-[18px] text-gray-500"
        @click="handleRegistrationsClick"
      >
        <div class="font-secondary">+{{ registrations.length - 3 }} more</div>
        <ChevronDownIcon class="size-2.5 text-gray-500" />
      </div>
    </div>
  </div>

  <div
    class="flex min-h-[30px] w-full items-start justify-between border-b border-[#E5E5E5] py-1.5 text-[12px] leading-[16px] text-gray-800"
  >
    <div class="opacity-60">{{ pluralize('Owner', owners.length) }}</div>
    <div class="max-w-[180px] text-right font-secondary">
      {{ owners.join(', ') }}
    </div>
  </div>

  <div
    class="flex min-h-[30px] w-full items-start justify-between border-b border-[#E5E5E5] py-1.5 text-[12px] leading-[16px] text-gray-800"
  >
    <div class="opacity-60">{{ pluralize('Author', authors.length) }}</div>
    <div class="max-w-[180px] text-right font-secondary">
      {{ authors.join(', ') || '—' }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Avatar, Thumbnail } from '@dacr/library'
import {
  DacrMarkIcon,
  FileTextIcon,
  ChevronRightIcon,
  ChevronDownIcon,
} from '@dacr/icons'
import {
  CopyrightStatus,
  FileRole,
  type File,
} from '@/shared/graphql/router/graphql'
import { openDialog, Dialog } from '@/shared/dialogs/dialog'
import { formatTimestamp } from '@/shared/utils/datetime'
import { applyAuthorizationToken } from '@/shared/utils/url'
import { fileTypeToThumbnail } from '@/shared/utils/thumbnail'
import pluralize from 'pluralize'

const props = defineProps<{
  file: File
}>()

const previewImageUrl = computed(() =>
  props.file.asset?.previewImageUrl
    ? applyAuthorizationToken(props.file.asset.previewImageUrl, [
        { key: 'width', value: 300 },
      ])
    : undefined
)

const thumbType = computed(() => fileTypeToThumbnail(props.file.type))

const isInactive = computed(
  () => props.file.copyright?.status === CopyrightStatus.Inactive
)

const canViewCertificates = computed(() => props.file.role !== FileRole.Public)

const fileInformation = computed(() => [
  {
    label: 'Size',
    value: props.file.metadata?.size || 0,
  },
  {
    label: 'Type',
    value: props.file.type,
  },
  {
    label: 'Visibility',
    value: isInactive.value
      ? '—'
      : props.file.settings?.isPrivate
        ? 'Private'
        : 'Public',
  },
])

const registrations = computed(() => props.file.copyright?.registrations ?? [])

const displayedRegistrations = computed(() => registrations.value.slice(0, 3))

const hasMoreRegistrations = computed(() => registrations.value.length > 3)

const owners = computed(() => {
  const ownerNames: string[] = []
  if (props.file.owner?.name) ownerNames.push(props.file.owner.name)
  props.file.coOwners?.edges?.forEach((edge) => {
    if (edge.node.__typename === 'EntityWithRole' && edge.node.entity.name) {
      ownerNames.push(edge.node.entity.name)
    }
  })
  return ownerNames
})

const authors = computed(() => {
  const authorNames: string[] = []
  props.file.authors?.edges?.forEach((edge) => {
    if (edge.node.__typename === 'EntityWithRole' && edge.node.entity.name) {
      authorNames.push(edge.node.entity.name)
    }
  })
  return authorNames
})

const handleRegistrationsClick = () => {
  openDialog(Dialog.Registrations, {
    stack: true,
    props: { file: props.file },
  })
}
</script>
